import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, renderList as _renderList, normalizeClass as _normalizeClass, unref as _unref, vModelCheckbox as _vModelCheckbox, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = { class: "cdoc__head" }
const _hoisted_2 = { key: 0 }
const _hoisted_3 = { class: "cdoc__head-actions" }
const _hoisted_4 = ["disabled"]
const _hoisted_5 = {
  key: 0,
  class: "cdoc__lead"
}
const _hoisted_6 = { class: "cdoc__toc" }
const _hoisted_7 = ["onClick"]
const _hoisted_8 = ["id"]
const _hoisted_9 = { class: "cdoc__group-head" }
const _hoisted_10 = {
  key: 1,
  class: "cdoc__party"
}
const _hoisted_11 = { class: "cdoc__party-list" }
const _hoisted_12 = { class: "cdoc__party-row" }
const _hoisted_13 = {
  key: 1,
  class: "is-muted"
}
const _hoisted_14 = { class: "cdoc__party-row" }
const _hoisted_15 = { class: "cdoc__method-boxes" }
const _hoisted_16 = { class: "cdoc__box" }
const _hoisted_17 = ["checked"]
const _hoisted_18 = ["disabled"]
const _hoisted_19 = {
  key: 0,
  class: "cdoc__hint"
}
const _hoisted_20 = { class: "cdoc__party-row" }
const _hoisted_21 = { class: "cdoc__party-row" }
const _hoisted_22 = {
  key: 0,
  class: "cdoc__sig"
}
const _hoisted_23 = ["src"]
const _hoisted_24 = {
  key: 3,
  class: "cdoc__check cdoc__check--section"
}
const _hoisted_25 = ["onUpdate:modelValue"]
const _hoisted_26 = {
  key: 4,
  class: "cdoc__check cdoc__check--section"
}
const _hoisted_27 = ["onUpdate:modelValue"]
const _hoisted_28 = {
  key: 1,
  id: "cdoc-final",
  class: "cdoc__check cdoc__check--final"
}

import { computed, defineComponent, h, reactive, ref, watch } from 'vue'
import AppIcon from '@/components/common/AppIcon.vue'
import {
  parseContractBodySegments,
  parseContractSections,
  type ContractBodySegment,
} from '@/utils/parseContractSections'
import { downloadLegalText } from '@/utils/legalAcceptancePreview'
import { downloadContractPdf } from '@/utils/contractPdf'

export type ContractCheckProgress = {
  key: string
  label: string
  done: boolean
  target: string
}


export default /*@__PURE__*/_defineComponent({
  __name: 'ContractDocumentView',
  props: {
    title: {},
    version: { default: '' },
    effective: { default: '' },
    body: {},
    mode: { default: 'view' },
    party: {},
    signature: { default: '' },
    signedAt: { default: '' },
    signerName: { default: '' },
    downloadStoredPdf: {}
  },
  emits: ["update:ready", "update:checks"],
  setup(__props: any, { expose: __expose, emit: __emit }) {

const ContractBodySegments = defineComponent({
  name: 'ContractBodySegments',
  props: {
    body: { type: String, default: '' },
  },
  setup(props) {
    return () => {
      const segments = parseContractBodySegments(props.body)
      if (!segments.length) {
        return h('div', { class: 'cdoc__body cdoc__body--block' }, '(내용 없음)')
      }
      return h(
        'div',
        { class: 'cdoc__segments' },
        segments.map((seg, index) => renderSegment(seg, index)),
      )
    }
  },
})

function renderSegment(seg: ContractBodySegment, index: number) {
  if (seg.type === 'prose') {
    return h('div', { key: `p-${index}`, class: 'cdoc__body cdoc__body--block' }, seg.text)
  }
  return h('div', { key: `t-${index}`, class: 'cdoc__table-wrap' }, [
    h('table', { class: 'cdoc__table' }, [
      h(
        'thead',
        h(
          'tr',
          seg.headers.map((cell) => h('th', cell)),
        ),
      ),
      h(
        'tbody',
        seg.rows.map((row, ri) =>
          h(
            'tr',
            { key: ri },
            row.map((cell) => h('td', cell || '\u00a0')),
          ),
        ),
      ),
    ]),
  ])
}

const props = __props

const hasStoredPdf = computed(() => typeof props.downloadStoredPdf === 'function')

const emit = __emit

const groups = computed(() => parseContractSections(props.body))
const contentGroups = computed(() => groups.value.filter((g) => g.kind !== 'intro'))
const introSubtitle = computed(() => {
  const intro = groups.value.find((g) => g.kind === 'intro')
  return intro?.blocks[0]?.body || ''
})

const checked = reactive<Record<string, boolean>>({})
/** 온라인 온보딩은 전자계약만 사용. 서면은 표시만 하고 선택 불가 */
const writtenMethod = ref(false)
const electronicMethod = ref(props.mode === 'view')
const finalAgree = ref(false)
const pdfBusy = ref(false)

watch(
  groups,
  (next) => {
    next.forEach((group) => {
      if (group.requiresCheck && checked[group.id] === undefined) {
        checked[group.id] = props.mode === 'view'
      }
    })
    if (props.mode === 'view') {
      electronicMethod.value = true
      finalAgree.value = true
    }
  },
  { immediate: true },
)

const partyLine = computed(() => {
  const p = props.party
  if (!p) return ''
  const parts = [p.storeName, p.ownerName, p.bizNumber].filter(Boolean)
  return parts.join(' / ')
})

const partyAddress = computed(() => {
  const p = props.party
  if (!p?.address) return ''
  return [p.address, p.addressDetail].filter(Boolean).join(' ')
})

const signedMeta = computed(() => {
  if (!props.signedAt && !props.signerName) return ''
  return [props.signedAt, props.signerName].filter(Boolean).join(' · ')
})

const ready = computed(() => {
  if (props.mode === 'view') return true
  if (!electronicMethod.value || !finalAgree.value) return false
  return groups.value
    .filter((g) => g.requiresCheck)
    .every((g) => checked[g.id])
})

function checkLabel(group: { id: string; title: string; kind: string }) {
  if (group.kind === 'basic') return '기본계약 확인'
  if (group.kind === 'party') return '계약당사자 확인'
  const annex = group.title.match(/^별첨\s*\d+/)
  if (annex) return `${annex[0]} 확인`
  return `${shortTitle(group.title)} 확인`
}

const checkProgress = computed<ContractCheckProgress[]>(() => {
  if (props.mode === 'view') return []
  const items: ContractCheckProgress[] = groups.value
    .filter((g) => g.requiresCheck)
    .map((g) => ({
      key: g.id,
      label: checkLabel(g),
      done: Boolean(checked[g.id]),
      target: `cdoc-${g.id}`,
    }))
  items.push({
    key: 'electronic',
    label: '전자계약 방식',
    done: electronicMethod.value,
    target: 'cdoc-electronic',
  })
  items.push({
    key: 'final',
    label: '최종 동의',
    done: finalAgree.value,
    target: 'cdoc-final',
  })
  return items
})

watch(ready, (value) => emit('update:ready', value), { immediate: true })
watch(checkProgress, (items) => emit('update:checks', items), { immediate: true, deep: true })

function shortTitle(title: string) {
  return title.replace(/^별첨\s*/, '별첨 ').slice(0, 28)
}

function scrollTo(id: string) {
  document.getElementById(`cdoc-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

function downloadTxt() {
  downloadLegalText({
    code: 'seller_supply_contract',
    version: props.version || 'contract',
    body: props.body,
    title: props.title,
  })
}

async function downloadPdf() {
  if (pdfBusy.value) return
  pdfBusy.value = true
  try {
    if (props.downloadStoredPdf) {
      await props.downloadStoredPdf()
      return
    }
    await downloadContractPdf({
      title: props.title,
      version: props.version,
      effective: props.effective,
      body: props.body,
      party: props.party,
      signature: props.signature,
      signedAt: props.signedAt,
      signerName: props.signerName,
      electronicMethod: electronicMethod.value || props.mode === 'view',
    })
  } catch (error) {
    console.error(error)
    window.alert(
      error instanceof Error
        ? error.message
        : '계약서 PDF를 처리하지 못했습니다. 잠시 후 다시 시도해 주세요.',
    )
  } finally {
    pdfBusy.value = false
  }
}

__expose({ ready, downloadPdf })

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["cdoc", { 'is-view': __props.mode === 'view' }])
  }, [
    _createElementVNode("header", _hoisted_1, [
      _createElementVNode("div", null, [
        _createElementVNode("strong", null, _toDisplayString(__props.title), 1),
        (__props.version)
          ? (_openBlock(), _createElementBlock("em", _hoisted_2, [
              _createTextVNode("v" + _toDisplayString(__props.version), 1),
              (__props.effective)
                ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                    _createTextVNode(" · 시행 " + _toDisplayString(__props.effective), 1)
                  ], 64))
                : _createCommentVNode("", true)
            ]))
          : _createCommentVNode("", true)
      ]),
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("button", {
          type: "button",
          class: "sui-btn sui-btn--sm",
          onClick: downloadTxt
        }, [
          _createVNode(AppIcon, {
            icon: "mdi-download",
            size: 16
          }),
          _cache[2] || (_cache[2] = _createTextVNode(" 텍스트 ", -1))
        ]),
        _createElementVNode("button", {
          type: "button",
          class: "sui-btn sui-btn--sm sui-btn--primary",
          disabled: pdfBusy.value,
          onClick: downloadPdf
        }, [
          _createVNode(AppIcon, {
            icon: "mdi-file-pdf-box",
            size: 16
          }),
          _createTextVNode(" " + _toDisplayString(pdfBusy.value
              ? hasStoredPdf.value
                ? 'PDF 받는 중…'
                : 'PDF 만드는 중…'
              : hasStoredPdf.value
                ? '체결 PDF 저장'
                : 'PDF로 저장'), 1)
        ], 8, _hoisted_4)
      ])
    ]),
    (introSubtitle.value)
      ? (_openBlock(), _createElementBlock("p", _hoisted_5, _toDisplayString(introSubtitle.value), 1))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_6, [
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(contentGroups.value, (group) => {
        return (_openBlock(), _createElementBlock("button", {
          key: `toc-${group.id}`,
          type: "button",
          class: _normalizeClass(["cdoc__toc-item", { 'is-done': __props.mode === 'view' || checked[group.id] || !group.requiresCheck }]),
          onClick: ($event: any) => (scrollTo(group.id))
        }, _toDisplayString(shortTitle(group.title)), 11, _hoisted_7))
      }), 128))
    ]),
    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(contentGroups.value, (group) => {
      return (_openBlock(), _createElementBlock("section", {
        id: `cdoc-${group.id}`,
        key: group.id,
        class: "cdoc__group"
      }, [
        _createElementVNode("header", _hoisted_9, [
          _createElementVNode("h3", null, _toDisplayString(group.title), 1),
          (group.requiresCheck && __props.mode === 'agree')
            ? (_openBlock(), _createElementBlock("span", {
                key: 0,
                class: _normalizeClass(["cdoc__badge", { 'is-on': checked[group.id] }])
              }, _toDisplayString(checked[group.id] ? '확인함' : '확인 필요'), 3))
            : _createCommentVNode("", true)
        ]),
        (group.kind === 'basic')
          ? (_openBlock(true), _createElementBlock(_Fragment, { key: 0 }, _renderList(group.blocks, (block) => {
              return (_openBlock(), _createElementBlock("details", {
                key: block.id,
                class: "cdoc__article",
                open: ""
              }, [
                _createElementVNode("summary", null, _toDisplayString(block.title), 1),
                _createVNode(_unref(ContractBodySegments), {
                  body: block.body
                }, null, 8, ["body"])
              ]))
            }), 128))
          : (group.kind === 'party')
            ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
                _createElementVNode("dl", _hoisted_11, [
                  _cache[9] || (_cache[9] = _createElementVNode("div", { class: "cdoc__party-row" }, [
                    _createElementVNode("dt", null, "갑"),
                    _createElementVNode("dd", null, "주식회사 함우리 / 대표자 김승식 / 사업자등록번호 185-81-02287")
                  ], -1)),
                  _cache[10] || (_cache[10] = _createElementVNode("div", { class: "cdoc__party-row" }, [
                    _createElementVNode("dt", null, "갑 주소"),
                    _createElementVNode("dd", null, "전북특별자치도 전주시 덕진구 기지로 77, 202동 2803호")
                  ], -1)),
                  _createElementVNode("div", _hoisted_12, [
                    _cache[3] || (_cache[3] = _createElementVNode("dt", null, "을", -1)),
                    _createElementVNode("dd", null, [
                      (partyLine.value)
                        ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                            _createTextVNode(_toDisplayString(partyLine.value) + " ", 1),
                            (partyAddress.value)
                              ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                  _createTextVNode(" / " + _toDisplayString(partyAddress.value), 1)
                                ], 64))
                              : _createCommentVNode("", true)
                          ], 64))
                        : (_openBlock(), _createElementBlock("span", _hoisted_13, "[상호 / 대표자 / 사업자등록번호 / 주소]"))
                    ])
                  ]),
                  _createElementVNode("div", _hoisted_14, [
                    _cache[6] || (_cache[6] = _createElementVNode("dt", null, "계약방식", -1)),
                    _createElementVNode("dd", _hoisted_15, [
                      _createElementVNode("label", _hoisted_16, [
                        _createElementVNode("input", {
                          type: "checkbox",
                          checked: writtenMethod.value,
                          disabled: ""
                        }, null, 8, _hoisted_17),
                        _cache[4] || (_cache[4] = _createElementVNode("span", null, "서면계약", -1))
                      ]),
                      _createElementVNode("label", {
                        id: "cdoc-electronic",
                        class: _normalizeClass(["cdoc__box", { 'is-pick': __props.mode === 'agree' }])
                      }, [
                        _withDirectives(_createElementVNode("input", {
                          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((electronicMethod).value = $event)),
                          type: "checkbox",
                          disabled: __props.mode === 'view'
                        }, null, 8, _hoisted_18), [
                          [_vModelCheckbox, electronicMethod.value]
                        ]),
                        _cache[5] || (_cache[5] = _createElementVNode("span", null, "카이온 전자약관·전자계약", -1))
                      ], 2)
                    ])
                  ]),
                  (__props.mode === 'agree')
                    ? (_openBlock(), _createElementBlock("p", _hoisted_19, " 온라인 가입은 「카이온 전자약관·전자계약」에 체크해야 합니다. (서면계약은 이 화면에서 선택하지 않습니다.) "))
                    : _createCommentVNode("", true),
                  _createElementVNode("div", _hoisted_20, [
                    _cache[7] || (_cache[7] = _createElementVNode("dt", null, "계약 버전", -1)),
                    _createElementVNode("dd", null, _toDisplayString(__props.version || '[시스템 자동표시]'), 1)
                  ]),
                  _createElementVNode("div", _hoisted_21, [
                    _cache[8] || (_cache[8] = _createElementVNode("dt", null, "체결일·동의자", -1)),
                    _createElementVNode("dd", null, _toDisplayString(signedMeta.value || '[시스템 자동기록 / 성명·직책·본인확인]'), 1)
                  ])
                ]),
                (__props.signature)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_22, [
                      _cache[11] || (_cache[11] = _createElementVNode("span", null, "서명", -1)),
                      _createElementVNode("img", {
                        src: __props.signature,
                        alt: "서명"
                      }, null, 8, _hoisted_23)
                    ]))
                  : _createCommentVNode("", true)
              ]))
            : (_openBlock(true), _createElementBlock(_Fragment, { key: 2 }, _renderList(group.blocks, (block) => {
                return (_openBlock(), _createElementBlock("div", {
                  key: block.id,
                  class: "cdoc__annex"
                }, [
                  _createVNode(_unref(ContractBodySegments), {
                    body: block.body
                  }, null, 8, ["body"])
                ]))
              }), 128)),
        (group.requiresCheck && __props.mode === 'agree' && group.kind !== 'party')
          ? (_openBlock(), _createElementBlock("label", _hoisted_24, [
              _withDirectives(_createElementVNode("input", {
                "onUpdate:modelValue": ($event: any) => ((checked[group.id]) = $event),
                type: "checkbox"
              }, null, 8, _hoisted_25), [
                [_vModelCheckbox, checked[group.id]]
              ]),
              _createTextVNode(" " + _toDisplayString(group.kind === 'basic' ? '기본계약(제1조~제16조) 내용을 확인했습니다' : `${shortTitle(group.title)} 내용을 확인했습니다`), 1)
            ]))
          : (group.requiresCheck && __props.mode === 'agree' && group.kind === 'party')
            ? (_openBlock(), _createElementBlock("label", _hoisted_26, [
                _withDirectives(_createElementVNode("input", {
                  "onUpdate:modelValue": ($event: any) => ((checked[group.id]) = $event),
                  type: "checkbox"
                }, null, 8, _hoisted_27), [
                  [_vModelCheckbox, checked[group.id]]
                ]),
                _cache[12] || (_cache[12] = _createTextVNode(" 계약당사자 정보와 전자계약 방식을 확인했습니다 ", -1))
              ]))
            : _createCommentVNode("", true)
      ], 8, _hoisted_8))
    }), 128)),
    (__props.mode === 'agree')
      ? (_openBlock(), _createElementBlock("label", _hoisted_28, [
          _withDirectives(_createElementVNode("input", {
            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((finalAgree).value = $event)),
            type: "checkbox"
          }, null, 512), [
            [_vModelCheckbox, finalAgree.value]
          ]),
          _cache[13] || (_cache[13] = _createTextVNode(" 공동구매 상품 공급 및 판매운영 기본계약서와 별첨 1~5의 전체 내용을 확인하였고, 계약체결 권한을 가지고 이에 동의합니다. ", -1))
        ]))
      : _createCommentVNode("", true)
  ], 2))
}
}

})